import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {DecimalPipe} from "@angular/common";
import {CcBaseComponent} from "../../../../crystal-elements/cc-base.component";
import {FormFieldGroupConfig} from "../../../../model/api/form-field-group-config";
import {FieldRecord} from "../../../../model/api/field-record";
import {ReportProfile} from "../../../../model/archive/report-profile";
import {FormFieldConfig} from "../../../../model/api/form-field-config";
import {BatchUpdateRequest} from "../../../../model/api/batch-update-request";
import {CcFormDialogComponent} from "../../../../crystal-elements/form-dialog/cc-form-dialog.component";
import {User} from "../../../../model/security/user";
import {CCRolesType} from "../../../../model/security/cc-roles-type";
import {SecurityUserService} from "../../../../services/security/security-user.service";
import TYPE = FormFieldConfig.TYPE;
import {BatchUtils} from "../../../../utils/batch-utils";

@Component({
  selector: 'app-batch-update-user-form',
  templateUrl: './batch-update-user-form.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
    DecimalPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchUpdateUserFormComponent extends CcBaseComponent {

  formLoading: boolean = false;

  formSubmitted: boolean = true;

  formConfig: FormFieldGroupConfig[] = [];

  dialogFormTitle: string = '';

  @Input()
  authorities: FieldRecord<any>[];
  @Input()
  userGroups: FieldRecord<any>[];
  @Input()
  notifications: FieldRecord<any>[];

  @Input()
  countries: FieldRecord<any>[];

  @Input()
  languages: FieldRecord<any>[];

  @Input()
  formRecord: User;
  @Input()
  transPartKey: string;
  @Input()
  selectedRecords: User[] = [];
  @Input()
  dialogFormVisible: boolean = false;
  @Output()
  dialogFormVisibleChange = new EventEmitter<boolean>();

  @Output()
  onSavedForm = new EventEmitter<any>();

  @ViewChild('batchUpdateFormDialog')
  ccFormDialog: CcFormDialogComponent;

  fieldsToUpdate: string[] = [];

  constructor(cdref: ChangeDetectorRef,
              private securityUserService: SecurityUserService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.initFormConfigs();
    this.dialogFormTitle = this.getTranslation('page.' + this.transPartKey + '.table.batch_update_button');
  }


  protected initFormConfigs() {

    this.formConfig = [
      {
        group: '',
        fields: [
          {
            field: 'enabled',
            column: 3,
            type: TYPE.CHECKBOX,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('enabled')
          },
          {
            type: TYPE.NONE,
            column: 9,
          },
          {
            field: 'country',
            filter: true,
            column: 3,
            type: TYPE.DROPDOWN,
            icon: record => record ? 'flag-icon flag-icon-' + record.toLowerCase() : 'fas fa-globe-europe',
            options: () => this.countries,
            changed: event => this.formRecord.country = event.value.toUpperCase(),
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('country')
          },
          {
            field: 'language',
            filter: true,
            column: 3,
            type: TYPE.DROPDOWN,
            icon: record => record ? 'flag-icon flag-icon-' + record.toLowerCase() : 'fas fa-globe-europe',
            options: () => this.languages,
            changed: event => this.formRecord.country = event.value.toUpperCase(),
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('language')
          },
          {
            field: 'jobTitle',
            column: 3,
            type: TYPE.TEXT,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('jobTitle')
          }]
      },
      {
        group: 'roles',
        fields: [
          {
            field: 'roles',
            filter: true,
            column: 6,
            type: TYPE.MULTISELECT_GROUP,
            options: () => this.authorities,
            changed: (event, event2) => {
              if (event && event.value) {
                this.formRecord.roles = event.value;
              } else
                this.formRecord.roles = [];
            },
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('roles')
          },
        ]
      },
      {
        group: 'userGroupNames',
        fields: [
          {
            field: 'userGroupNames',
            filter: true,
            column: 6,
            type: TYPE.MULTISELECT,
            options: () => this.userGroups,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('userGroupNames')
          },
        ]
      },
      {
        group: 'info',
        fields: [
          {
            field: 'accountExpired',
            column: 4,
            // disabled: value => true,
            type: TYPE.CHECKBOX,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('accountExpired')
          },
          {
            field: 'accountLocked',
            column: 4,
            // disabled: value => true,
            type: TYPE.CHECKBOX,
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('accountLocked')
          },
          // {
          //   field: 'credentialsExpired',
          //   column: 4,
          //   // disabled: value => true,
          //   type: TYPE.CHECKBOX
          // },
        ]
      },
      {
        group: 'admin',
        fields: [
          {
            field: 'forceClearCookies',
            column: 3,
            // disabled: value => true,
            type: TYPE.CHECKBOX,
            changed: (event, event2) => {
              this.formRecord.webUserSettings.forceClearCookies = this.formRecord['forceClearCookies'];
            },
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('forceClearCookies')
          },
          {
            field: 'mailNotification',
            column: 3,
            // disabled: value => true,
            type: TYPE.CHECKBOX,
            changed: (event, event2) => {
              this.formRecord.webUserSettings.mailNotification = this.formRecord['mailNotification'];
            },
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, event.field, event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('mailNotification')
          },
          {
            field: 'notifActionTypesJs',
            filter: true,
            column: 6,
            type: TYPE.MULTISELECT_GROUP,
            optionValue: 'id',
            options: () => this.notifications,
            changed: event => {
              this.formRecord.webUserSettings.notifActionTypesJs = event.value;
            },
            labelCheckbox: true,
            labelCheckboxChanged: (event: FormFieldConfig, event2: any) => BatchUtils.labelCheckboxChanged(this.fieldsToUpdate, this.ccFormDialog, 'notifActionTypes', event2.checked),
            disabled: (record: any) => !this.fieldsToUpdate.includes('notifActionTypes')
          },
          {
            column: 0,
            type: TYPE.NONE,
            validate: () => this.validate()
          }
        ]
      }
    ];
  }

  closeDialogForm(): void {
    this.dialogFormVisible = false;
    this.dialogFormVisibleChange.emit(false);
    this.markForCheck();
  }

  save() {
    this.formLoading = true;
    const batchUpdateRequest = new BatchUpdateRequest<User>();
    batchUpdateRequest.recordToApply = {...this.formRecord}
    batchUpdateRequest.recordIds = this.selectedRecords.map(value => value.username);
    batchUpdateRequest.fieldToUpdate = [...this.fieldsToUpdate];

    this.securityUserService.saveBatch(batchUpdateRequest).subscribe(validation => {
      if (validation.success) {
        this.fieldsToUpdate.splice(0)
        this.onSavedForm.emit(this.formRecord);
        this.closeDialogForm();
      } else {
        this.errorMessage(validation.message);
      }
      this.formLoading = false;
    });
  }

  dialogFormVisibleChanged($event: boolean) {
    this.dialogFormVisibleChange.emit($event);
    this.fieldsToUpdate.splice(0);
  }

  validate(): string {
    return this.fieldsToUpdate.length == 0 ? 'fail' : '';
  }
}
